import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { StateService } from "../../../services/user/state.services";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import { CountryService } from "../../../services/user/country.services";
import Select from 'react-select';

const AddStateComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateId = location.state?.stateId || 0;
  const isEditMode = !!stateId;
  const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  const formik = useFormik({
    initialValues: {
      stateName: "",
      countryId: 0,
      isActive: true,
      storeId: selectedStore,
    },
    validationSchema: Yup.object({
      stateName: Yup.string().required("Please enter a valid name"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true)
        const data = { ...values, countryId: Number(values.countryId) }
        if (isEditMode) {
          await StateService.updateStateById(stateId, data)
            .then((response) => {
              if (response) {
                navigate("/state");
              }
            })
        } else {
          await StateService.addNewState(data)
            .then((response) => {
              if (response) {
                navigate("/state");
              }
            });
        }
      } catch (error) {
        console.error("Error while submitting form:", error);
        handleNotify(
          "An error occurred while saving the state.",
          TOASTER_TYPE.ERROR,
          TOASTER_POSITION.TOP_RIGHT
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (isEditMode) {
      const fetchStateDetail = async () => {
        try {
          const data = await StateService.getStateById(stateId);
          formik.setValues({
            stateId: data?.stateId,
            stateName: data?.stateName,
            countryId: data?.country.countryId,
            isActive: data?.isActive ?? true,
            storeId: data?.selectedStore,
          })
        }
        catch (error) {
          console.log('Error fetching State details', error)
        }
      }
      fetchStateDetail()
    }
  }, [])

  const fetchCountry = async () => {
    try {
      const response = await CountryService.getAllCountry(selectedStore);
      if (response) {
        const sortedCountry = response.sort((a, b) => a.countryId - b.countryId);
        const countryIdOptions = sortedCountry.map(country => ({
          value: country.countryId, // ID for select
          label: country.countryName, // Name to display
        }));
        if (!formik.values.countryId && countryIdOptions.length > 0) {
          formik.setFieldValue("countryId", countryIdOptions[0].value);
        }
        setCountries(countryIdOptions);
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  useEffect(() => {
    fetchCountry();
  }, [selectedStore]);

  const handleBackClick = () => {
    navigate("/state");
  };

  return (
    <div className="page-wrapper m-0 p-4">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center mb-3">
          <div
            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
            onClick={handleBackClick}
          >
            States</div>
          <div className="ps-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 p-0">
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? "Edit State" : "New State"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
              onClick={handleBackClick}
            >
              Back
            </button>
          </div>
        </div>

        <div className="card p-4 radius-10 border-top border-0 border-4 border-primary">
          <div className="card-body form-container">
            <form onSubmit={formik.handleSubmit} className="mt-4">
              {/* Name Field */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="stateName" className="form-label d-flex">
                    Name <span className="text-danger">*</span>:
                  </label>
                  <input
                    type="text"
                    id="stateName"
                    name="stateName"
                    className={`form-control ${formik.touched.stateName && formik.errors.stateName ? "is-invalid" : ""}`}
                    placeholder="Enter State Name"
                    value={formik.values.stateName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.stateName && formik.errors.stateName && (
                    <div className="invalid-feedback d-flex">{formik.errors.stateName}</div>
                  )}
                </div>
              </div>

              {/* Country Field */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="countryId" className="form-label d-flex">
                    Country:
                  </label>
                  <div className="text-start">
                    <Select
                      id="countryId"
                      name="countryId"
                      options={countries}
                      value={countries.find(option => option.value === formik.values.countryId)}
                      onChange={(selectedOption) => formik.setFieldValue('countryId', selectedOption.value)}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>
              </div>

              {/* Status Radio Buttons */}
              <div className="d-flex align-items-center mb-4">
                <label className="form-label me-3">Status:</label>
                <div className="form-check form-check-inline me-3">
                  <input
                    type="radio"
                    id="isActive"
                    name="isActive"
                    className="form-check-input"
                    value={true}
                    checked={formik.values.isActive === true}
                    onChange={() => formik.setFieldValue("isActive", true)}
                  />
                  <label className="form-check-label" htmlFor="active">
                    Active
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    id="inactive"
                    name="isActive"
                    className="form-check-input"
                    value={false}
                    checked={formik.values.isActive === false}
                    onChange={() => formik.setFieldValue("isActive", false)}
                  />
                  <label className="form-check-label" htmlFor="inactive">
                    Inactive
                  </label>
                </div>
              </div>

              {/* Submit */}
              <hr />
              <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                  onClick={handleBackClick}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStateComponent;